
/**
 * SiteSearch for lbbw-am.de
 *
 * @author Florian Gutwald
 * Converted to Plain JS for Rollup
 */
import Handlebars from 'handlebars';
import _ from 'lodash';
import AMApp from '../../global/app';
import Pagination from '../../gui/Pagination';

export default class SiteSearch {
  /**
   * SiteSearch constructor
   *
   * @param {HTMLElement} element - The site search container element
   * @param {Object} options - Configuration options
   */
  constructor(element, options = {}) {
    console.log('SiteSearch');
    this.element = element;
    this.amApp = new AMApp();

    this.settings = Object.assign({
      routeSearch: AMApp.langGetParam + 'eID=fonds&function=searchFull&search='
    }, options);

    this.resultPageSize = 20;
    this.hasResult = null;
    this.urlParams = new URLSearchParams(window.location.search);

    // DOM Elements
    this.searchInput = null;
    this.searchResult = null;
    this.searchButton = null;
    this.statusInit = null;

    this.paginationInstances = {};

    this.init();
  }

  /**
   * Initialize
   */
  init() {
    this.registerHandlebarsHelpers();
    this.cacheDOM();
    this.attachEventListeners();
    this.initSiteSearch();
  }

  /**
   * Register Handlebars helpers
   */
  registerHandlebarsHelpers() {
    Handlebars.registerHelper("cleanString", (str) => {
      str = Handlebars.escapeExpression(str);
      return new Handlebars.SafeString(str);
    });
  }

  /**
   * Cache DOM elements
   */
  cacheDOM() {
    this.searchInput = document.getElementById('site_search_input');
    this.searchResult = document.getElementById('searchresult');
    this.searchButton = document.getElementById('site_search_button');
    this.statusInit = document.querySelector('.js_status_init');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Search button click
    if (this.searchButton) {
      this.searchButton.addEventListener('click', () => {
        if (this.searchInput) {
          this.submitSearch(this.searchInput.value);
        }
      });
    }

    // Search input keypress (Enter)
    if (this.searchInput) {
      this.searchInput.addEventListener('keypress', (e) => {
        if (e.which === 13 || e.key === 'Enter') {
          this.submitSearch(this.searchInput.value);
        }
      });

      // Show results on focus if they exist
      this.searchInput.addEventListener('click', () => {
        this.handleSearchInputFocus();
      });

      this.searchInput.addEventListener('focus', () => {
        this.handleSearchInputFocus();
      });
    }

    // Click outside to close results
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#site_search_input') && this.searchResult) {
        this.searchResult.classList.remove('result__active');
      }
    });
  }

  /**
   * Handle search input focus
   */
  handleSearchInputFocus() {
    if (this.hasResult) {
      const searchResultElement = document.getElementById('search_result');
      if (searchResultElement) {
        searchResultElement.classList.add('search-result__active');
      }
    }
  }

  /**
   * Initialize site search
   */
  initSiteSearch() {
    const value = this.getSearchString();
    if (this.searchInput) {
      this.searchInput.value = value;
    }

    if (value) {
      this.getSearchResult(value);
    }
  }

  /**
   * Submit search
   */
  submitSearch(value) {
    if (value && value.trim() !== '') {
      this.getSearchResult(value);
    }
  }

  /**
   * Get search result
   */
  async getSearchResult(searchInput) {
    // Hide button and show loader
    if (this.searchButton) {
      this.searchButton.style.display = 'none';
    }

    if (this.searchResult) {
      this.searchResult.insertAdjacentHTML('afterend', AMApp.LOADING_ANIMATION);
      this.searchResult.classList.remove('searchresult-content__active');
    }

    if (this.searchButton) {
      this.searchButton.insertAdjacentHTML('afterend', AMApp.LOADING_ANIMATION);
    }

    if (this.statusInit) {
      this.statusInit.style.display = 'none';
    }

    // Build request URL with target parameter
    let parameter = this.amApp.isInstitutionell() ? '&target=I' : '&target=R';
    const request = AMApp.basePath + this.settings.routeSearch + searchInput + parameter;

    try {
      const response = await fetch(request);
      if (!response.ok) throw new Error('Network response was not ok');

      const result = await response.json();
      setTimeout(() => {
        this.renderSearchResult(result);
      }, 250);
    } catch (error) {
      console.error('Error fetching search results:', error);
      this.setStatusError();
    }
  }

  /**
   * Render search result
   */
  renderSearchResult(result) {
    try {
      this.createDataAndPager(result.fondsDocs, '#fonds');
      this.createDataAndPager(result.contentDocs, '#content');
      this.createDataAndPager(result.downloads, '#downloads');
      this.setStatusResult(result);
      this.setResultCount(result);
    } catch (e) {
      console.error(e);
      this.setStatusError();
    }
  }

  /**
   * Create data and pager
   */
  createDataAndPager(data, type) {
    const pager = type + '_pager';
    const template = type + '_searchresult_template';
    const target = type + '_searchresult_target';

    // Update result count
    const resultsEl = document.querySelector(type + '_results');
    if (resultsEl) {
      resultsEl.textContent = '(' + data.length + ')';
    }

    // Use native pagination
    const pagerEl = document.querySelector(pager);

    if (data.length > this.resultPageSize && pagerEl) {
      this.paginationInstances[type] = new Pagination(pagerEl, {
        dataSource: data,
        pageSize: this.resultPageSize,
        callback: (dataFiltered) => {
          this.renderData(template, target, dataFiltered);
        }
      });
    } else {
      this.renderData(template, target, data);
      if (pagerEl) {
        pagerEl.innerHTML = '';
      }
    }
  }

  /**
   * Render the data
   */
  renderData(template, target, data) {
    const templateEl = document.querySelector(template);
    const targetEl = document.querySelector(target);

    if (templateEl && targetEl) {
      const raw = templateEl.innerHTML;
      const tpl = Handlebars.compile(this.amApp.toHandlebarsDelimiters(raw));
      targetEl.innerHTML = tpl({ data: data });
    }
  }

  /**
   * Get search string from URL
   */
  getSearchString() {
    const pathname = window.location.pathname;

    if (this.urlParams.has('search')) {
      return this.urlParams.get('search');
    }

    if (/\/suche\//.test(pathname) || /\/search\//.test(pathname)) {
      const pathParts = pathname.split('/');
      if (AMApp.isGerman) {
        return pathParts[2];
      }
      return pathParts[3];
    }

    return undefined;
  }

  /**
   * Set status result
   */
  setStatusResult(result) {
    if (this.searchButton) {
      this.searchButton.style.display = 'block';
    }

    if (this.searchResult) {
      this.searchResult.classList.add('searchresult-content__active');
    }

    if (this.statusInit) {
      this.statusInit.style.display = 'none';
    }

    this.hasResult = true;
    this.removeLoader();

    // Toggle panes based on results
    if (result.fondsResults > 0) {
      this.togglePane('fonds', true);
    } else {
      this.togglePane('fonds', false);
    }

    if (result.fondsResults === 0 && result.contentResults > 0) {
      this.togglePane('content', true);
    } else {
      this.togglePane('content', false);
    }

    // Disable/enable buttons based on results
    const fondsToggle = document.querySelector('[data-toggle="fonds"]');
    if (fondsToggle) {
      if (result.fondsResults === 0) {
        fondsToggle.setAttribute('disabled', 'disabled');
      } else {
        fondsToggle.removeAttribute('disabled');
      }
    }

    const contentToggle = document.querySelector('[data-toggle="content"]');
    if (contentToggle) {
      if (result.contentResults === 0) {
        contentToggle.setAttribute('disabled', 'disabled');
      } else {
        contentToggle.removeAttribute('disabled');
      }
    }

    // Handle URL parameter for open pane
    if (this.urlParams.has('openpane')) {
      const openPane = this.urlParams.get('openpane');
      this.togglePane(openPane, true);

      // Close other panes
      const otherPanes = _.difference(['fonds', 'content', 'downloads'], [openPane]);
      otherPanes.forEach(pane => {
        this.togglePane(pane, false);
      });
    }
  }

  /**
   * Set status error
   */
  setStatusError() {
    if (this.searchButton) {
      this.searchButton.style.display = 'block';
    }

    if (this.searchResult) {
      this.searchResult.classList.remove('searchresult-content__active');
    }

    const headerSearchResult = document.getElementById('header_search_result_template');
    if (headerSearchResult) {
      headerSearchResult.style.display = 'none';
    }

    this.hasResult = false;
    this.removeLoader();
  }

  /**
   * Toggle pane visibility
   */
  togglePane(target, boolean) {
    const toggleTarget = document.querySelector(`[data-toggle-target="${target}"]`);
    const toggleButton = document.querySelector(`[data-toggle="${target}"]`);

    if (toggleTarget) {
      if (boolean) {
        toggleTarget.classList.add('toggle__active');
      } else {
        toggleTarget.classList.remove('toggle__active');
      }
    }

    if (toggleButton) {
      if (boolean) {
        toggleButton.classList.add('gui--toggle__active');
      } else {
        toggleButton.classList.remove('gui--toggle__active');
      }
    }
  }

  /**
   * Set result count for each category
   */
  setResultCount(result) {
    const fondsResults = document.getElementById('fonds_results');
    const contentResults = document.getElementById('content_results');
    const downloadsResults = document.getElementById('downloads_results');

    if (fondsResults) {
      fondsResults.textContent = `(${result.fondsResults})`;
    }
    if (contentResults) {
      contentResults.textContent = `(${result.contentResults})`;
    }
    if (downloadsResults) {
      downloadsResults.textContent = `(${result.downloadsResults})`;
    }
  }

  /**
   * Remove loader elements
   */
  removeLoader() {
    // Remove loader after searchresult
    if (this.searchResult) {
      const nextLoader = this.searchResult.nextElementSibling;
      if (nextLoader && nextLoader.classList.contains('loader')) {
        nextLoader.remove();
      }
    }

    // Remove all loaders in site_search
    const siteSearchEl = document.getElementById('site_search');
    if (siteSearchEl) {
      const loaders = siteSearchEl.querySelectorAll('.loader');
      loaders.forEach(loader => loader.remove());
    }
  }

  /**
   * Destroy the SiteSearch instance
   */
  destroy() {
    this.removeLoader();
  }
}
